import { ChangeDetectionStrategy, Component, effect, inject, model, output } from '@angular/core';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { NotificationStore } from './notification.store';
import { Notification, NotificationTaskStatus, NotificationType } from '@raptor-fe/core';
import { DatePipe } from '@angular/common';
import { NamedRoutes, RouterService, TranslationPipe } from '@raptor-fe/services';

@Component({
    selector: 'lib-web-notification-panel',
    imports: [KENDO_LAYOUT, KENDO_BUTTONS, DatePipe, TranslationPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `      
      <kendo-drawer-container>
        <kendo-drawer
          #drawer
          position="end"
          [expanded]="isVisible()"
          [width]="350"
          (collapse)="isVisible.set(false)"
        >
        <ng-template kendoDrawerTemplate>
            <div class="p-3">
                <h5 class="justify-content-between d-flex">
                  Notifications Panel                  
                  <button kendoButton class="float-end" look="flat" iconClass="bi bi-x-lg" size="large" (click)="isVisible.set(false)"> </button>
                </h5>
            </div>
            @for (item of store.results(); track $index) {
              <div class="m-2 border-bottom">
                <h6>
                  @if(item.type === NotificationType.LongRunningTask){                  
                    @if(item.relatedTaskStatus === NotificationTaskStatus.Completed){
                      <i class="bi bi-check-circle-fill text-success me-2"></i>
                    }
                    @else if(item.relatedTaskStatus === NotificationTaskStatus.Failed){
                      <i class="bi bi-x-circle-fill text-danger me-2"></i>
                    }
                    @else if(item.relatedTaskStatus === NotificationTaskStatus.Running){
                      <span class="spinner-grow spinner-grow-sm text-primary me-2" role="status" aria-hidden="true"></span>
                    }
                  }                
                  {{ item.title }}
                  <button kendoButton class="float-end" look="flat" size="small" iconClass="bi bi-x" (click)="markAsDismissed(item)"> </button>
                </h6>
                <p class="text-xs">{{ item.message }}</p>
                @if (item.type === NotificationType.CustomerMessage) {
                  <button
                    kendoButton
                    class="w-100 mb-2"
                    themeColor="primary"
                    fillMode="outline"
                    size="small"
                    iconClass="bi bi-chat-dots"
                    (click)="openCustomerMessage(item)"
                  >
                    {{ { feature: 'Customer Messaging', component: 'Notification Panel', label: 'Open in Message Center' } | translate }}
                  </button>
                }
                <p class="text-xs text-muted text-end">{{ item.modifiedDate | date:'short' }}</p>
              </div>
            }
          </ng-template>
        </kendo-drawer>      
      </kendo-drawer-container>
      `,
})
export class WebNotificationPanelComponent {
    protected readonly store = inject(NotificationStore);
    private readonly routerService = inject(RouterService);
    isVisible = model.required<boolean>();
    notificationsChange = output<number>();

    protected NotificationType = NotificationType;
    protected NotificationTaskStatus = NotificationTaskStatus;

    constructor() {
        effect(() => {
            this.notificationsChange.emit(this.store.notificationCount());
        });

        this.store.fetchNotifications();
    }

    protected markAsDismissed(item: Notification): void {
        if (item?.id) this.store.markAsDismissed({ notificationId: item.id as string });
    }

    /**
     * Handles a click on a CustomerMessage notification. Deep-links into the Message Center passing
     * the referent so the left conversation list auto-selects it -- a customer id when the inbound
     * message matched a customer (`referenceType === 'Customer'`), or a phone number for an unmatched
     * inbound (`referenceType === 'Phone'`). Opening the message is the user acting on the
     * notification, so it is dismissed (removed from the panel, bell badge decremented) rather than
     * merely marked read, and the panel is closed. Typed text elsewhere is untouched.
     */
    protected openCustomerMessage(item: Notification): void {
        const customerId = item.referenceType === 'Customer' ? item.referenceId : undefined;
        const phone = item.referenceType === 'Phone' ? item.referenceId : undefined;

        void this.routerService.navigateTo(NamedRoutes.messageCenter(customerId, phone));

        if (item?.id) {
            this.store.markAsDismissed({ notificationId: item.id as string });
        }

        this.isVisible.set(false);
    }
}
